/**
 * Tutorial guiado — lógica pura (próximo passo, normalização).
 * Sem DOM, sem import do app.js. Testável em Node.
 */

import {
  DROPS_VALUE_THRESHOLD,
  TUTORIAL_PHASES,
  TUTORIAL_STEPS,
  phaseById,
  phaseTriggers,
  stepById,
  stepsForPhase,
} from './tutorial-steps.mjs?v=20260727-atlas-guide-3';

export const TUTORIAL_VERSION = 2;
export { DROPS_VALUE_THRESHOLD };

export function emptyTutorial() {
  return {
    version: TUTORIAL_VERSION,
    done: [],
    skipped: false,
    phase: null,
    current: null,
    startedAt: null,
  };
}

/** Normaliza blob corrompido / versões antigas. */
export function normalizeTutorial(raw) {
  const base = emptyTutorial();
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return base;
  const done = Array.isArray(raw.done)
    ? [...new Set(raw.done.filter((id) => typeof id === 'string' && id))]
    : [];
  const version = Math.max(1, Math.floor(Number(raw.version) || TUTORIAL_VERSION));
  const startedAt = raw.startedAt == null ? null : Math.max(0, Number(raw.startedAt) || 0);
  const phase = raw.phase == null || raw.phase === '' ? null : String(raw.phase);
  const current = raw.current == null || raw.current === '' ? null : String(raw.current);
  return {
    version,
    done,
    skipped: !!raw.skipped,
    phase: phase && phaseById(phase) ? phase : null,
    current: current && stepById(current) ? current : null,
    startedAt: Number.isFinite(startedAt) && startedAt > 0 ? startedAt : null,
  };
}

/**
 * Reinicia uma Ãºnica vez contas que ainda carregam uma versÃ£o anterior.
 * `hasStarter` evita abrir o tutorial antes de uma conta nova concluir o onboarding.
 */
export function migrateTutorialVersion(raw, hasStarter, now = Date.now()) {
  const rawVersion = Math.max(0, Math.floor(Number(raw?.version) || 0));
  const next = normalizeTutorial(raw);
  if (rawVersion >= TUTORIAL_VERSION || !hasStarter) return next;
  return {
    ...emptyTutorial(),
    startedAt: now,
    phase: 'core',
  };
}

function safeSkipIf(step) {
  if (typeof step.skipIf !== 'function') return false;
  try {
    return !!step.skipIf();
  } catch {
    return false;
  }
}

function meetsRequires(step, state) {
  if (typeof step.requires !== 'function') return true;
  try {
    return !!step.requires(state);
  } catch {
    return false;
  }
}

export function isPhaseDone(tutorial, phaseId, steps = TUTORIAL_STEPS) {
  const next = normalizeTutorial(tutorial);
  const phaseSteps = stepsForPhase(phaseId, steps);
  if (!phaseSteps.length) return true;
  return phaseSteps.every((step) => next.done.includes(step.id));
}

export function priorsDone(tutorial, phase, phases = TUTORIAL_PHASES, steps = TUTORIAL_STEPS) {
  const next = normalizeTutorial(tutorial);
  return phases
    .filter((entry) => entry.order < phase.order)
    .every((entry) => isPhaseDone(next, entry.id, steps));
}

function matchesPhaseGate(phase, state, eventName, payload = {}) {
  const triggers = phaseTriggers(phase);
  if (eventName != null) {
    if (!triggers.includes(eventName)) return false;
  } else if (triggers.length && phase.minLevel == null && phase.minDropValue == null) {
    return false;
  }

  if (phase.minLevel != null) {
    const level = Number(state?.trainer?.level) || 1;
    if (level < phase.minLevel) return false;
  }
  if (phase.minDropValue != null) {
    const value = Number(payload.value);
    if (!Number.isFinite(value) || value < phase.minDropValue) return false;
  }
  return true;
}

/**
 * Abre a próxima fase elegível (ou limpa fase concluída).
 * eventName null = só predicados de nível (poll).
 */
export function maybeStartNextPhase(tutorial, state, eventName = null, payload = {}, now = Date.now(), phases = TUTORIAL_PHASES, steps = TUTORIAL_STEPS) {
  let next = normalizeTutorial(tutorial);
  if (next.skipped) return next;

  if (next.phase && isPhaseDone(next, next.phase, steps)) {
    next = { ...next, phase: null, current: null };
  }
  if (next.phase) return next;

  const ordered = [...phases].sort((a, b) => a.order - b.order);
  for (const phase of ordered) {
    if (isPhaseDone(next, phase.id, steps)) continue;
    if (!priorsDone(next, phase, phases, steps)) continue;
    if (!matchesPhaseGate(phase, state, eventName, payload)) continue;
    return {
      ...next,
      phase: phase.id,
      current: null,
      startedAt: next.startedAt || now,
    };
  }
  return next;
}

/** @deprecated use maybeStartNextPhase — mantido para compat dos testes. */
export function beginPhaseFromEvent(tutorial, eventName, now = Date.now(), phases = TUTORIAL_PHASES) {
  return maybeStartNextPhase(tutorial, { trainer: { level: 99 }, stats: {} }, eventName, { value: DROPS_VALUE_THRESHOLD }, now, phases);
}

/**
 * Próximo passo a exibir, ou null.
 * `exclude` = ids descartados só nesta sessão (não viram `done`; voltam depois).
 */
export function nextStep(state, steps = TUTORIAL_STEPS, phases = TUTORIAL_PHASES, exclude = null) {
  if (!state || typeof state !== 'object') return null;
  const tutorial = normalizeTutorial(state.tutorial);
  if (tutorial.skipped) return null;

  const done = new Set(tutorial.done);
  const skip = exclude instanceof Set ? exclude : new Set(Array.isArray(exclude) ? exclude : []);
  const usable = (step) => !done.has(step.id) && !skip.has(step.id)
    && meetsRequires(step, state) && !safeSkipIf(step);

  if (tutorial.current) {
    const current = stepById(tutorial.current, steps);
    if (current && usable(current)) return current;
  }

  const phaseId = tutorial.phase;
  if (!phaseId) return null;
  if (!phaseById(phaseId, phases)) return null;

  for (const step of stepsForPhase(phaseId, steps)) {
    if (usable(step)) return step;
  }
  return null;
}

/** Ids de passos ainda não feitos que o skipIf pularia agora. */
export function skippedByLayout(state, steps = TUTORIAL_STEPS) {
  const tutorial = normalizeTutorial(state?.tutorial);
  if (tutorial.skipped || !tutorial.phase) return [];
  const done = new Set(tutorial.done);
  return stepsForPhase(tutorial.phase, steps)
    .filter((step) => !done.has(step.id) && meetsRequires(step, state) && safeSkipIf(step))
    .map((step) => step.id);
}

export function markStepDone(tutorial, stepId) {
  const next = normalizeTutorial(tutorial);
  if (!stepId || typeof stepId !== 'string') return next;
  if (!next.done.includes(stepId)) next.done = [...next.done, stepId];
  if (next.current === stepId) next.current = null;
  return next;
}

export function setCurrentStep(tutorial, stepId) {
  const next = normalizeTutorial(tutorial);
  next.current = stepId || null;
  return next;
}

export function skipTutorial(tutorial) {
  const next = normalizeTutorial(tutorial);
  next.skipped = true;
  next.current = null;
  next.phase = null;
  return next;
}

/** Reinicia o tutorial (botão "Rever tutorial"). */
export function restartTutorialProgress(tutorial, now = Date.now()) {
  return {
    ...emptyTutorial(),
    startedAt: now,
    phase: 'core',
  };
}

/**
 * Marca todas as fases conhecidas como feitas (migração de save antigo).
 */
export function markAllPhasesDone(tutorial, steps = TUTORIAL_STEPS) {
  const next = normalizeTutorial(tutorial);
  next.done = [...new Set(steps.map((step) => step.id))];
  next.phase = null;
  next.current = null;
  next.skipped = false;
  return next;
}

export function shouldMigrateVeteran(state) {
  if (!state || typeof state !== 'object') return false;
  const tutorial = normalizeTutorial(state.tutorial);
  if (tutorial.done.length > 0 || tutorial.skipped || tutorial.phase || tutorial.current) return false;
  const level = Number(state.trainer?.level) || 1;
  const captured = Number(state.stats?.captured) || 0;
  return level > 1 || captured > 0;
}

export function phaseProgress(tutorial, phaseId, steps = TUTORIAL_STEPS) {
  const next = normalizeTutorial(tutorial);
  const phaseSteps = stepsForPhase(phaseId, steps);
  const doneCount = phaseSteps.filter((step) => next.done.includes(step.id)).length;
  return { total: phaseSteps.length, done: doneCount, currentIndex: Math.min(doneCount + 1, phaseSteps.length || 1) };
}
